import type {
  AgentPrices,
  AgentPromptTemplate,
  AgentRunRequest,
  AgentRunResponse,
  AgentScope,
  AgentTemplateEstimate,
  AgentVariablesResponse,
  CustomAgent,
  CustomAgentCreate,
  CustomAgentUpdate,
  OnboardingDraftResponse,
  OnboardingRequest,
  AgentOnboardingStatusResponse,
  OrgProfile,
  OrgProfileData,
} from '@/types';
import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/**
 * RTK Query slice for the Agents Consolidation V1 (CLE-321) endpoints.
 *
 * Covers:
 *   - Onboarding hard-gate probe + start
 *   - Org profile read/write (admin gate enforced server-side)
 *   - Catalog + template resolution + estimate (run modal chips)
 *   - Run dispatch + status poll
 *
 * On run success this slice ALSO invalidates the smart-columns slice
 * (SmartColumnResults tag) so the drawer + table refresh without the
 * caller having to coordinate two stores manually.
 */
export const agentsApi = createApi({
  reducerPath: 'agentsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'OnboardingStatus',
    'OrgProfile',
    'AgentCatalog',
    'AgentTemplate',
    'AgentRun',
    'AgentVariables',
    'CustomAgent',
  ],
  endpoints: (builder) => ({
    // ---------- Onboarding ---------------------------------------------

    getOnboardingStatus: builder.query<AgentOnboardingStatusResponse, void>({
      query: () => ({
        url: '/agents/onboarding/status',
        method: 'GET',
      }),
      providesTags: ['OnboardingStatus'],
    }),

    startOnboarding: builder.mutation<
      OnboardingDraftResponse,
      OnboardingRequest
    >({
      query: (body) => ({
        url: '/agents/onboarding',
        method: 'POST',
        body,
      }),
      // Don't invalidate status yet -- onboarding draft is created but
      // onboarding_completed_at stays null until the admin clicks Approve
      // (PUT /agents/profile with mark_onboarded=true). OrgProfile is
      // invalidated though, since the draft is persisted.
      invalidatesTags: ['OrgProfile'],
    }),

    // ---------- Profile -------------------------------------------------

    getProfile: builder.query<OrgProfile, void>({
      query: () => ({
        url: '/agents/profile',
        method: 'GET',
      }),
      providesTags: ['OrgProfile'],
    }),

    updateProfile: builder.mutation<
      OrgProfile,
      {
        website_url?: string;
        linkedin_url?: string;
        profile_data?: Partial<OrgProfileData>;
        /** Set to true ONLY from the Approve action in OnboardingSetup.
         *  Partial profile edits from the Profile tab must NOT pass
         *  this -- otherwise tweaking a single field would silently
         *  open the org-wide onboarding gate. */
        mark_onboarded?: boolean;
      }
    >({
      query: (body) => ({
        url: '/agents/profile',
        method: 'PUT',
        body,
      }),
      // Profile edit can also flip onboarding_completed_at to non-null
      // (the Approve action when mark_onboarded=true), so invalidate
      // the status query too.
      invalidatesTags: ['OrgProfile', 'OnboardingStatus'],
      // The sequencing settings count the profile's products, value props
      // and customer proof (what a Clu email step may use), so they are
      // read again too.
      async onQueryStarted(_body, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { engageApi } = await import('./engage.feature');
          dispatch(engageApi.util.invalidateTags(['EngageSequencingSettings']));
        } catch {
          // Mutation failed; nothing to invalidate.
        }
      },
    }),

    // ---------- Catalog + Templates -------------------------------------

    getTemplate: builder.query<
      AgentPromptTemplate,
      { agent_type: string; scope?: AgentScope }
    >({
      query: ({ agent_type, scope }) => {
        const params = new URLSearchParams();
        if (scope) params.set('scope', scope);
        const qs = params.toString();
        return {
          url: `/agents/templates/${agent_type}${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, { agent_type, scope }) => [
        { type: 'AgentTemplate', id: `${agent_type}:${scope ?? 'none'}` },
      ],
    }),

    saveTemplateOverride: builder.mutation<
      AgentPromptTemplate,
      { agent_type: string; prompt_template: string; scope?: AgentScope }
    >({
      query: ({ agent_type, ...body }) => ({
        url: `/agents/templates/${agent_type}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { agent_type, scope }) => [
        'AgentTemplate',
        { type: 'AgentTemplate', id: `${agent_type}:${scope ?? 'none'}` },
      ],
    }),

    /**
     * Resolves the template + counts tokens + computes per-lead credit
     * cost. Called from the AgentRunPanel credit + token chips on every
     * prompt edit (debounced upstream). A `builder.query` so RTK caches
     * by full args -- repeated resolves for the same prompt are free
     * and don't re-hit the BE.
     */
    getTemplateEstimate: builder.query<
      AgentTemplateEstimate,
      {
        agent_type: string;
        scope?: AgentScope;
        prompt_override?: string;
        lead_count?: number;
      }
    >({
      query: (body) => ({
        url: '/agents/templates/estimate',
        method: 'POST',
        body,
      }),
    }),

    /**
     * Every agent's per-record price, each from /agents/templates/estimate
     * (the BE prices it exactly as a run is charged): research per scope,
     * qualification, a custom agent on the starter prompt, and each saved
     * custom agent on its own prompt, with how each research price is
     * estimated. Refetched when a template or a saved agent changes. A saved
     * agent whose estimate fails is left out (its price stays hidden)
     * instead of failing every other price.
     */
    getAgentPrices: builder.query<
      AgentPrices,
      {
        // Part of the cache key only: prices differ per org (AppSumo-only
        // orgs pay 3x), so a switched org never reads the last org's prices.
        organizationId: string;
        customAgents: Array<{ id: string; prompt: string }>;
      }
    >({
      async queryFn({ customAgents }, _api, _extra, baseQuery) {
        const estimate = async (body: {
          agent_type: string;
          scope?: AgentScope;
          prompt_override?: string;
        }): Promise<AgentTemplateEstimate> => {
          const res = await baseQuery({
            url: '/agents/templates/estimate',
            method: 'POST',
            body,
          });
          if (res.error) throw res.error;
          return res.data as AgentTemplateEstimate;
        };
        try {
          const [company, contact, both, qualification, custom, saved] =
            await Promise.all([
              estimate({ agent_type: 'research', scope: 'company' }),
              estimate({ agent_type: 'research', scope: 'contact' }),
              estimate({ agent_type: 'research', scope: 'both' }),
              estimate({ agent_type: 'qualification' }),
              estimate({ agent_type: 'custom' }),
              Promise.allSettled(
                customAgents.map(
                  async ({ id, prompt }) =>
                    [
                      id,
                      (
                        await estimate({
                          agent_type: 'custom',
                          prompt_override: prompt || undefined,
                        })
                      ).credits_per_lead,
                    ] as const
                )
              ),
            ]);
          return {
            data: {
              research: {
                company: company.credits_per_lead,
                contact: contact.credits_per_lead,
                both: both.credits_per_lead,
              },
              researchEstimate: {
                company: company.estimate ?? null,
                contact: contact.estimate ?? null,
                both: both.estimate ?? null,
              },
              qualification: qualification.credits_per_lead,
              custom: custom.credits_per_lead,
              customAgents: Object.fromEntries(
                saved.flatMap((result) =>
                  result.status === 'fulfilled' ? [result.value] : []
                )
              ),
            },
          };
        } catch (error) {
          return { error: error as FetchBaseQueryError };
        }
      },
      providesTags: ['AgentTemplate', { type: 'CustomAgent', id: 'LIST' }],
    }),

    // ---------- Run dispatch --------------------------------------------

    runAgent: builder.mutation<AgentRunResponse, AgentRunRequest>({
      query: (body) => ({
        url: '/agents/run',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { lead_list_id }) => [
        'AgentRun',
        { type: 'AgentRun', id: lead_list_id },
      ],
      // Cross-invalidate the smart-columns slice so the drawer + the
      // leads table refresh as new processed_value rows land. The agents
      // run path funnels through SmartColumnWorkflow on the BE -- same
      // results table, same WS broadcasts.
      async onQueryStarted({ lead_list_id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { smartColumnsApi } = await import('./smart-columns.feature');
          dispatch(
            smartColumnsApi.util.invalidateTags([
              { type: 'SmartColumnResults', id: lead_list_id },
              // Company-scope agent runs land in the company-results table;
              // invalidate the namespaced company tag too so the Companies
              // sheet refreshes as rows complete. Harmless for contact-scope
              // runs (no company rows → empty refetch).
              { type: 'SmartColumnResults', id: `company:${lead_list_id}` },
            ])
          );
        } catch {
          // Mutation failed; nothing to invalidate.
        }
      },
    }),

    // ---------- Variable catalog (slash-picker source) ------------------

    getAgentVariables: builder.query<AgentVariablesResponse, void>({
      query: () => ({ url: '/agents/variables', method: 'GET' }),
      providesTags: ['AgentVariables'],
      // Static-ish catalog -- keep the cache around so the picker
      // popover opens instantly on subsequent invocations.
      keepUnusedDataFor: 60 * 60,
    }),

    // ---------- Custom Agent CRUD --------------------------------------

    listCustomAgents: builder.query<
      CustomAgent[],
      void | { organizationId: string }
    >({
      query: (scope) => ({
        url: '/agents/custom-agents',
        method: 'GET',
        ...(scope
          ? { headers: { 'X-Cleanlist-Expected-Org': scope.organizationId } }
          : {}),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({
                type: 'CustomAgent' as const,
                id: a.id,
              })),
              { type: 'CustomAgent' as const, id: 'LIST' },
            ]
          : [{ type: 'CustomAgent' as const, id: 'LIST' }],
    }),

    getCustomAgent: builder.query<CustomAgent, string>({
      query: (id) => ({ url: `/agents/custom-agents/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'CustomAgent', id }],
    }),

    createCustomAgent: builder.mutation<CustomAgent, CustomAgentCreate>({
      query: (body) => ({
        url: '/agents/custom-agents',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'CustomAgent', id: 'LIST' }],
    }),

    updateCustomAgent: builder.mutation<
      CustomAgent,
      { id: string; patch: CustomAgentUpdate }
    >({
      query: ({ id, patch }) => ({
        url: `/agents/custom-agents/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'CustomAgent', id },
        { type: 'CustomAgent', id: 'LIST' },
      ],
    }),

    deleteCustomAgent: builder.mutation<void, string>({
      query: (id) => ({
        url: `/agents/custom-agents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'CustomAgent', id },
        { type: 'CustomAgent', id: 'LIST' },
      ],
    }),

    /**
     * LLM-backed prompt scaffolder. The user types a plain-English
     * description; the BE returns a structured prompt draft using XML
     * sections and the variable catalog. Pure mutation -- no caching,
     * every call is a fresh draft (regeneration is meaningful UX, not a
     * cache miss).
     */
    draftPrompt: builder.mutation<
      { prompt: string; estimated_tokens: number },
      {
        description: string;
        agent_type: string;
        scope?: AgentScope;
      }
    >({
      query: (body) => ({
        url: '/agents/draft-prompt',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetOnboardingStatusQuery,
  useStartOnboardingMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetTemplateQuery,
  useSaveTemplateOverrideMutation,
  useLazyGetTemplateEstimateQuery,
  useGetAgentPricesQuery,
  useRunAgentMutation,
  useGetAgentVariablesQuery,
  useListCustomAgentsQuery,
  useGetCustomAgentQuery,
  useCreateCustomAgentMutation,
  useUpdateCustomAgentMutation,
  useDraftPromptMutation,
  useDeleteCustomAgentMutation,
} = agentsApi;
